import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { Link, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Card, ConfirmationDialog, DataTable, FormSelect, StatusBadge, TradingStrategySelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import {
    formatAeroCompactNumber,
    formatAeroJobDisplayName,
    formatAeroStatus,
    getAeroStatusTone,
    isAeroJobActive,
    isAeroJobTerminal,
    parsePositiveInt,
    type AeroJobStatus
} from './aeroPageUtils';
import {
    getAeroJobExitConfigsScored,
    formatAeroJobTimestamp,
    getAeroJobProgressPercent,
    getAeroJobWorkUnitProgressPercent,
    summarizeAeroJobEntities
} from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobs';
import type { AeroOptimizationJobSummary } from '@/features/tradeLogicAuthoring/api';
import type { TradingStrategy } from '@einhaus/project314-types';

const STATUS_FILTERS: { label: string; value: 'all' | AeroJobStatus }[] = [
    { label: 'All statuses', value: 'all' },
    { label: 'Running', value: 'running' },
    { label: 'Queued', value: 'queued' },
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' },
    { label: 'Failed', value: 'failed' },
    { label: 'Cancelled', value: 'cancelled' }
];

const Aero = () => {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pendingDeleteJob, setPendingDeleteJob] = useState<AeroOptimizationJobSummary | null>(null);

    const requestedStrategyId = parsePositiveInt(searchParams.get('strategyId'));
    const statusFilter = parseStatusFilter(searchParams.get('status'));

    const jobsQuery = useAppQuery({
        ...trpc.exitOptimization.fetchJobs.queryOptions({}),
        refetchInterval: (query) => (hasActiveJobs(query.state.data?.jobs ?? []) ? 5000 : false),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const deleteJobMutation = useMutation({
        ...trpc.exitOptimization.deleteJob.mutationOptions(),
        onSuccess: async (result) => {
            if (result.success) {
                showToast('Deleted AERO job', 'success');
                setPendingDeleteJob(null);
                await queryClient.invalidateQueries(trpc.exitOptimization.fetchJobs.pathFilter());
                return;
            }

            showToast(result.errorMessages.join(' ') || 'AERO job could not be deleted', 'error');
        }
    });

    const jobs = useMemo(
        () => [...(jobsQuery.data?.jobs ?? [])].sort((left, right) => getSortableTime(right.createdAt) - getSortableTime(left.createdAt)),
        [jobsQuery.data?.jobs]
    );

    const availableStrategyIds = useMemo(() => new Set(jobs.map((job) => job.strategyId)), [jobs]);

    const isStrategyInJobs = useCallback((strategy: TradingStrategy) => availableStrategyIds.has(strategy.id), [availableStrategyIds]);

    const filteredJobs = useMemo(
        () =>
            jobs.filter((job) => {
                const matchesStrategy = requestedStrategyId === null || job.strategyId === requestedStrategyId;
                const matchesStatus = statusFilter === 'all' || job.status === statusFilter;

                return matchesStrategy && matchesStatus;
            }),
        [jobs, requestedStrategyId, statusFilter]
    );

    const stats = useMemo(() => buildDashboardStats(jobs), [jobs]);

    const columns = useMemo<ColumnDef<AeroOptimizationJobSummary>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'Job',
                cell: ({ row }) => <AeroJobNameCell job={row.original} />
            },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => (
                    <StatusBadge status={row.original.status} tone={getAeroStatusTone(row.original.status)}>
                        {formatAeroStatus(row.original.status)}
                    </StatusBadge>
                )
            },
            {
                accessorKey: 'strategyName',
                header: 'Strategy',
                cell: ({ row }) => (
                    <div>
                        <div className="font-medium text-zinc-100">{row.original.strategyName}</div>
                        <div className="text-xs text-zinc-500">#{row.original.strategyId}</div>
                    </div>
                )
            },
            {
                id: 'entities',
                header: 'Entities',
                cell: ({ row }) => <span className="text-zinc-300">{summarizeAeroJobEntities(row.original)}</span>
            },
            {
                id: 'progress',
                header: 'Progress',
                cell: ({ row }) => <AeroProgressBar job={row.original} />
            },
            {
                accessorKey: 'createdAt',
                header: 'Created',
                cell: ({ row }) => <span className="text-zinc-400">{formatAeroJobTimestamp(row.original.createdAt)}</span>
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" to={`/user/aero/${row.original.id}`} variant="outline-dark">
                            View
                        </Button>
                        <Button size="sm" to={`/user/aero/new?cloneFrom=${row.original.id}`} variant="outline-dark">
                            Duplicate
                        </Button>
                        <Button
                            disabled={!isAeroJobTerminal(row.original)}
                            size="sm"
                            variant="destructive"
                            onClick={() => setPendingDeleteJob(row.original)}
                        >
                            Delete
                        </Button>
                    </div>
                )
            }
        ],
        []
    );

    const updateStrategyFilter = (strategyId: number | null) => {
        const nextParams = new URLSearchParams(searchParams);

        if (strategyId !== null) nextParams.set('strategyId', String(strategyId));
        else nextParams.delete('strategyId');

        setSearchParams(nextParams);
    };

    const updateStatusFilter = (value: string) => {
        const nextParams = new URLSearchParams(searchParams);
        const parsedStatus = parseStatusFilter(value);

        if (parsedStatus === 'all') nextParams.delete('status');
        else nextParams.set('status', parsedStatus);

        setSearchParams(nextParams);
    };

    const confirmDeleteJob = () => {
        if (pendingDeleteJob === null) return;

        deleteJobMutation.mutate({ jobId: pendingDeleteJob.id });
    };

    return (
        <UserLayout containerSize="1400px" title="AERO Jobs - Project 314">
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-zinc-100">AERO Jobs</h1>
                    <p className="mt-1 text-sm text-zinc-400">Run Rust Trade Logic V2 exit optimization and review candidate results.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-dark" onClick={() => jobsQuery.refetch()}>
                        Refresh
                    </Button>
                    <Button size="sm" to={buildCreateJobUrl(requestedStrategyId)}>
                        New AERO Job
                    </Button>
                </div>
            </div>

            <AeroDashboardStats stats={stats} />

            <Card
                className="mt-4"
                headerActions={
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" to="/user/tradingStrategies" variant="outline-dark">
                            Strategies
                        </Button>
                        <Button size="sm" to="/user/tradeLogic" variant="outline-dark">
                            Trading Rules
                        </Button>
                    </div>
                }
                subtitle="Filter, open, and clean up optimization jobs."
                title="Job History"
            >
                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-[minmax(220px,1fr)_220px]">
                    <TradingStrategySelect
                        filter={isStrategyInJobs}
                        label="Strategy"
                        placeholder="All strategies"
                        value={requestedStrategyId}
                        onChange={updateStrategyFilter}
                    />
                    <FormSelect
                        label="Status"
                        options={STATUS_FILTERS}
                        value={statusFilter}
                        onChange={(event) => updateStatusFilter(event.target.value)}
                    />
                </div>

                {jobsQuery.isLoading ? (
                    <DataLoadingSpinner text="Loading AERO jobs..." />
                ) : jobsQuery.error instanceof Error ? (
                    <div className="rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-100">
                        {jobsQuery.error.message}
                    </div>
                ) : filteredJobs.length === 0 ? (
                    <AeroEmptyState hasJobs={jobs.length > 0} requestedStrategyId={requestedStrategyId} statusFilter={statusFilter} />
                ) : (
                    <DataTable search sort columns={columns} data={filteredJobs} pagination={{ enabled: true, pageSize: 20 }} />
                )}
            </Card>

            <ConfirmationDialog
                confirmText="Delete Job"
                isConfirmLoading={deleteJobMutation.isPending}
                isOpen={pendingDeleteJob !== null}
                message={
                    pendingDeleteJob === null
                        ? ''
                        : `Delete ${formatAeroJobDisplayName(pendingDeleteJob)}? This removes the job record and results from the AERO UI.`
                }
                title="Delete AERO Job"
                onCancel={() => setPendingDeleteJob(null)}
                onConfirm={confirmDeleteJob}
            />
        </UserLayout>
    );
};

const AeroDashboardStats = ({ stats }: { stats: ReturnType<typeof buildDashboardStats> }) => (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <AeroStatCard label="Total" value={formatAeroCompactNumber(stats.total)} />
        <AeroStatCard label="Active" tone="info" value={formatAeroCompactNumber(stats.active)} />
        <AeroStatCard label="Completed" tone="success" value={formatAeroCompactNumber(stats.completed)} />
        <AeroStatCard label="Failed" tone="danger" value={formatAeroCompactNumber(stats.failed)} />
        <AeroStatCard label="Exit configs" value={formatAeroCompactNumber(stats.totalCombinations)} />
    </div>
);

const AeroStatCard = ({
    label,
    tone = 'default',
    value
}: {
    label: string;
    tone?: 'default' | 'danger' | 'info' | 'success';
    value: string;
}) => {
    const toneClass =
        tone === 'success' ? 'text-green-300' : tone === 'danger' ? 'text-red-300' : tone === 'info' ? 'text-cyan-300' : 'text-zinc-100';

    return (
        <Card bodyClassName="p-4">
            <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
            <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
        </Card>
    );
};

const AeroJobNameCell = ({ job }: { job: AeroOptimizationJobSummary }) => (
    <Link className="block text-left hover:text-cyan-300" to={`/user/aero/${job.id}`}>
        <div className="font-medium text-zinc-100">{formatAeroJobDisplayName(job)}</div>
        <div className="text-xs text-zinc-500">#{job.id}</div>
        {job.description && <div className="mt-1 max-w-[280px] truncate text-xs text-zinc-500">{job.description}</div>}
    </Link>
);

const AeroProgressBar = ({ job }: { job: AeroOptimizationJobSummary }) => {
    const hasWorkUnits = job.totalWorkUnits > 0;
    const progressPercent = hasWorkUnits ? getAeroJobWorkUnitProgressPercent(job) : getAeroJobProgressPercent(job);

    const progressLabel = hasWorkUnits
        ? `${formatAeroCompactNumber(job.completedWorkUnits)} / ${formatAeroCompactNumber(job.totalWorkUnits)} units`
        : `${formatAeroCompactNumber(getAeroJobExitConfigsScored(job))} / ${formatAeroCompactNumber(job.totalCombinations)} configs`;

    return (
        <div className="min-w-[160px]">
            <div className="mb-1 flex items-center justify-between gap-2 text-xs text-zinc-500">
                <span>{progressPercent}%</span>
                <span>{progressLabel}</span>
            </div>
            <div className="h-2 rounded bg-zinc-800">
                <div className="h-2 rounded bg-cyan-600" style={{ width: `${progressPercent}%` }} />
            </div>
        </div>
    );
};

const AeroEmptyState = ({
    hasJobs,
    requestedStrategyId,
    statusFilter
}: {
    hasJobs: boolean;
    requestedStrategyId: number | null;
    statusFilter: 'all' | AeroJobStatus;
}) => (
    <div className="rounded-md border border-zinc-800 bg-black/20 p-6 text-center">
        <div className="text-sm font-medium text-zinc-100">{hasJobs ? 'No jobs match the current filters.' : 'No AERO jobs yet.'}</div>
        <div className="mt-2 text-sm text-zinc-500">
            {hasJobs
                ? 'Adjust the strategy or status filter to find older jobs.'
                : 'Create a job to evaluate exit candidates for a saved Trade Logic V2 strategy.'}
        </div>
        <Button className="mt-4" size="sm" to={buildCreateJobUrl(requestedStrategyId)} variant="outline-dark">
            {statusFilter === 'all' ? 'Create AERO Job' : 'Create New Job'}
        </Button>
    </div>
);

const parseStatusFilter = (value: string | null): 'all' | AeroJobStatus => {
    if (
        value === 'pending' ||
        value === 'running' ||
        value === 'completed' ||
        value === 'failed' ||
        value === 'cancelled' ||
        value === 'queued' ||
        value === 'cancelling'
    ) {
        return value;
    }

    return 'all';
};

const hasActiveJobs = (jobs: AeroOptimizationJobSummary[]) => jobs.some(isAeroJobActive);

const buildDashboardStats = (jobs: AeroOptimizationJobSummary[]) => ({
    active: jobs.filter(isAeroJobActive).length,
    completed: jobs.filter((job) => job.status === 'completed').length,
    failed: jobs.filter((job) => job.status === 'failed').length,
    total: jobs.length,
    totalCombinations: jobs.reduce((sum, job) => sum + job.totalCombinations, 0)
});

const buildCreateJobUrl = (strategyId: number | null) =>
    strategyId === null ? '/user/aero/new' : `/user/aero/new?strategyId=${strategyId}`;

const getSortableTime = (value: string | null) => {
    if (!value) return 0;

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
};

export default Aero;
